/**
 * The one-line verdict under a spot page's verdict block (spec section 5.15). Pure.
 *
 * Vocabulary matches Companion (lib/companion/decision.ts): go now at 7.5 and fresh, go later
 * from 5.5 (with the window label the page already shows, never one computed here), go
 * elsewhere when a same-area row beats the spot by at least ELSEWHERE_SCORE_GAP, and don't
 * bother below 5.5 with nothing better. A red flag reads "Check safety first", never go now.
 *
 * Ability rule (spec section 5.5): an advanced or expert spot is never named without its label.
 * For surf and bodyboarding, a spot that is not experienced only never sends people to an
 * experienced-only row; an experienced-only spot may, and the text then carries the label.
 */
import {
  COMPANION_GO_LATER_SCORE,
  COMPANION_GO_NOW_SCORE,
  ELSEWHERE_SCORE_GAP,
  type CompanionDecisionAction,
} from "@/lib/companion/decision";
import type { ConditionSafetyState } from "@/lib/conditions/safety-notes";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import { isExperiencedOnly, type AreaRow } from "./row-model";
import type { SurfDifficulty } from "./types";

export type SpotVerdictLine = Readonly<{
  action: CompanionDecisionAction;
  /** "Go now: 74." / "Go elsewhere: Watergate Bay is 81 today." */
  text: string;
  /** "Experienced surfers only: shallow reef." */
  abilityNote: string | null;
  elsewhere: Readonly<{ slug: string; displayName: string; score10: number; href: string; experiencedOnly: boolean }> | null;
}>;

export type SpotVerdictInput = Readonly<{
  sport: string;
  score10: number | null;
  freshness: "updated-today" | "updated-yesterday" | "stale" | "missing";
  safetyState: ConditionSafetyState | null;
  difficulty: SurfDifficulty | null;
  spotType: string | null;
  betterNearby: readonly AreaRow[];
  /** Only a window the page already shows today; never computed here. */
  windowLabel: string | null;
  localHour: number;
  /** Optional: the first safety note the page shows, quoted after "Check safety first". */
  safetyNote?: string | null;
}>;

function abilityNoteFor(input: SpotVerdictInput): string | null {
  if (!isExperiencedOnly(input.sport, input.difficulty, input.spotType)) return null;
  const who = input.sport === "bodyboarding" ? "bodyboarders" : "surfers";
  const where = input.spotType === "reef" ? "shallow reef" : input.spotType === "point" ? "point break" : null;
  return where ? `Experienced ${who} only: ${where}.` : `Experienced ${who} only.`;
}

function elsewhereText(sport: string, elsewhere: NonNullable<SpotVerdictLine["elsewhere"]>): string {
  const label = elsewhere.experiencedOnly ? ` (experienced ${sport === "bodyboarding" ? "bodyboarders" : "surfers"} only)` : "";
  return `Go elsewhere${label}: ${elsewhere.displayName} is ${scoreOutOf100(elsewhere.score10)} today.`;
}

function sentence(note: string): string {
  const trimmed = note.trim();
  return /[.!?]$/u.test(trimmed) ? trimmed : `${trimmed}.`;
}

export function buildSpotVerdictLine(input: SpotVerdictInput): SpotVerdictLine {
  const abilityNote = abilityNoteFor(input);
  const score = input.score10;
  const fresh = input.freshness === "updated-today" || input.freshness === "updated-yesterday";
  // abilityNote is non-null exactly when this spot is itself experienced only.
  const allowExperienced = abilityNote !== null;
  const better = input.betterNearby.find((row) => row.status === "ranked" && row.score10 !== null
    && row.safetyState !== "flagged-red"
    && (allowExperienced || !row.experiencedOnly)
    && (score === null || row.score10 - score >= ELSEWHERE_SCORE_GAP - 1e-9)) ?? null;
  const elsewhere = better && better.score10 !== null
    ? { slug: better.slug, displayName: better.displayName, score10: better.score10, href: better.href, experiencedOnly: better.experiencedOnly }
    : null;

  if (input.safetyState === "flagged-red") {
    const note = input.safetyNote?.trim() ? ` ${sentence(input.safetyNote)}` : " PeakWave has flagged a red safety risk today.";
    return {
      action: score !== null && score >= COMPANION_GO_LATER_SCORE ? "go-later" : "do-not-bother",
      text: `Check safety first.${note}`,
      abilityNote,
      elsewhere,
    };
  }
  if (score === null) {
    return elsewhere
      ? { action: "go-elsewhere", text: elsewhereText(input.sport, elsewhere), abilityNote, elsewhere }
      : { action: "do-not-bother", text: "No score yet today.", abilityNote, elsewhere: null };
  }
  const s = scoreOutOf100(score);
  if (elsewhere && fresh) {
    return { action: "go-elsewhere", text: elsewhereText(input.sport, elsewhere), abilityNote, elsewhere };
  }
  if (score >= COMPANION_GO_NOW_SCORE && fresh) {
    return { action: "go-now", text: input.windowLabel ? `Go now: ${s}, best ${input.windowLabel}.` : `Go now: ${s}.`, abilityNote, elsewhere: null };
  }
  if (score >= COMPANION_GO_LATER_SCORE) {
    const text = input.windowLabel
      ? `Go later: ${s}, best ${input.windowLabel}.`
      : `Go later: ${s}, useful but not urgent.`;
    return { action: "go-later", text, abilityNote, elsewhere: null };
  }
  return { action: "do-not-bother", text: `Don't bother today: ${s}.`, abilityNote, elsewhere: null };
}
